import { useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  ArrowLeft, 
  Trash2, 
  ShieldAlert, 
  Mail, 
  CheckCircle2,
  ExternalLink,
  Key,
  RotateCcw,
  UserX
} from "lucide-react";

export default function DataDeletion() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      {/* Back Button */}
      <div className="max-w-3xl mx-auto mb-8">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 font-black uppercase text-sm border-2 border-foreground bg-white px-4 py-2 shadow-brutal-sm hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-none transition-all"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>
      </div>

      <main className="max-w-3xl mx-auto border-4 border-foreground bg-white shadow-brutal overflow-hidden">
        {/* Header */}
        <div className="bg-primary p-8 border-b-4 border-foreground">
          <div className="w-16 h-16 bg-white border-4 border-foreground mb-6 flex items-center justify-center shadow-brutal-sm rotate-3">
            <Trash2 className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-4xl md:text-5xl font-black uppercase italic text-white tracking-tighter leading-none">
            Account & Data <br />Deletion
          </h1>
        </div>

        <div className="p-8 space-y-10">
          {/* Section 1: Intro */}
          <section>
            <span className="chip bg-mint border-2 border-foreground font-bold uppercase text-xs mb-4">Compliance</span>
            <p className="font-bold text-lg leading-relaxed mt-2">
              At DmFlio, we value your privacy. In accordance with Meta's Platform Policy, we provide multiple ways to manage, temporarily disable, or permanently delete your data.
            </p>
          </section>

          {/* Section 2: Deletion Methods */}
          <section className="space-y-6">
            <h2 className="text-2xl font-black uppercase italic border-b-4 border-foreground inline-block">How to delete your account</h2>
            
            <div className="grid gap-6">
              {/* Option 1: Temporary Self-Service */}
              <div className="border-4 border-foreground p-5 bg-peach/10 flex gap-4 items-start shadow-brutal-sm">
                <div className="bg-foreground text-white w-8 h-8 flex items-center justify-center font-black shrink-0">1</div>
                <div>
                  <h3 className="font-black uppercase text-sm flex items-center gap-2">
                    <Key className="w-4 h-4" /> Temporary Deletion (In-App)
                  </h3>
                  <p className="text-sm font-bold text-muted-foreground mt-1">
                    Users can temporarily delete their account by clicking the <span className="text-foreground">"Delete Account"</span> button in their dashboard. You must enter the <span className="text-foreground">OTP sent to your registered email</span> to authorize this action.
                  </p>
                </div>
              </div>

              {/* Option 2: Facebook Settings */}
              <div className="border-4 border-foreground p-5 bg-sky/10 flex gap-4 items-start shadow-brutal-sm">
                <div className="bg-foreground text-white w-8 h-8 flex items-center justify-center font-black shrink-0">2</div>
                <div>
                  <h3 className="font-black uppercase text-sm">Via Facebook Settings</h3>
                  <p className="text-sm font-bold text-muted-foreground mt-1">
                    Navigate to "Settings & Privacy" &gt; "Settings" &gt; "Apps and Websites" on your Facebook profile. Locate DmFlio and click "Remove".
                  </p>
                </div>
              </div>

              {/* Option 3: Permanent Manual Request */}
              <div className="border-4 border-foreground p-5 bg-mint/10 flex gap-4 items-start shadow-brutal-sm">
                <div className="bg-foreground text-white w-8 h-8 flex items-center justify-center font-black shrink-0">3</div>
                <div>
                  <h3 className="font-black uppercase text-sm flex items-center gap-2">
                    <UserX className="w-4 h-4" /> Permanent & Unrecoverable Deletion
                  </h3>
                  <p className="text-sm font-bold text-muted-foreground mt-1">
                    If you want your data to be <span className="text-foreground">completely unrecoverable</span>, please send a request to <span className="text-primary underline">hello@dmflio.com</span> with the subject "Permanent Account Deletion".
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3: Recovery Policy */}
          <section className="bg-foreground text-white p-6 -rotate-1 shadow-brutal-sm">
            <h2 className="text-xl font-black uppercase italic mb-4 flex items-center gap-2">
              <RotateCcw className="text-primary" /> Account Recovery
            </h2>
            <div className="font-bold text-sm space-y-4">
              <p>
                If you have temporarily deleted your account and wish to restore it:
              </p>
              <ul className="list-disc ml-5 space-y-2">
                <li>Send a recovery request to <span className="text-primary">hello@dmflio.com</span></li>
                <li>Your account will be restored within <span className="text-mint">48 hours</span></li>
                <li>Your ID and password will remain the same as they were previously</li>
              </ul>
            </div>
          </section>

          {/* Section 4: What is deleted */}
          <section className="border-4 border-foreground p-6">
            <h2 className="text-xl font-black uppercase italic mb-4 flex items-center gap-2">
              <ShieldAlert className="text-primary" /> Data Impact
            </h2>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 font-bold text-xs">
              <li className="flex items-center gap-2 border-2 border-foreground p-2 bg-white">
                <CheckCircle2 className="w-4 h-4 text-mint" /> Meta API Access Tokens
              </li>
              <li className="flex items-center gap-2 border-2 border-foreground p-2 bg-white">
                <CheckCircle2 className="w-4 h-4 text-mint" /> Instagram Metadata
              </li>
              <li className="flex items-center gap-2 border-2 border-foreground p-2 bg-white">
                <CheckCircle2 className="w-4 h-4 text-mint" /> Automation Rules
              </li>
              <li className="flex items-center gap-2 border-2 border-foreground p-2 bg-white">
                <CheckCircle2 className="w-4 h-4 text-mint" /> Comment Histories
              </li>
            </ul>
          </section>
        </div>

        {/* Footer info */}
        <div className="bg-butter p-6 border-t-4 border-foreground flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2 font-black uppercase text-xs">
            <Mail className="w-4 h-4" /> hello@dmflio.com
          </div>
          <a 
            href="https://facebook.com/settings?tab=applications" 
            target="_blank" 
            rel="noreferrer"
            className="flex items-center gap-2 font-black uppercase text-xs underline"
          >
            Manage Apps on Facebook <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </main>

      <footer className="max-w-3xl mx-auto mt-12 text-center text-xs font-black uppercase opacity-50 italic pb-8">
       
      </footer>
    </div>
  );
}